import { useFormik } from 'formik';
import React from 'react';
import { Button, CustomInput, Form, FormFeedback, FormGroup, Input, Col, Row } from 'reactstrap';
import { SubmitForm, submitFormDefaults } from 'types/talent-partners.types';
import { validate } from 'utils/form-utils';
import { convertIsoToUsa, convertUsaToIso } from 'utils/timezone-utils';
import * as Yup from 'yup';

const validationSchema = () =>
  Yup.object()
    .shape({
      legalName: Yup.string()
        .min(3, 'Should be at least 3 characters.')
        .max(25, 'Should be 25 characters or less.')
        .required('Field is required'),
      personallyCertified: Yup.boolean().required('Field is required'),
      date: Yup.string().required('Field is required'),
    })
    .required();

interface Props {
  step6?: SubmitForm | null;
  onNext: (step6: SubmitForm) => void;
  goBack: () => void;
}

export const TalentPartners6: React.FC<Props> = ({ step6, onNext, goBack }) => {
  const formik = useFormik<SubmitForm>({
    enableReinitialize: true,
    initialValues: step6
      ? {
          ...step6,
          date: convertIsoToUsa(step6.date),
        }
      : submitFormDefaults(),
    validateOnMount: true,
    onSubmit: (step6) =>
      onNext({
        ...step6,
        date: convertUsaToIso(step6.date),
      }),
    validate: validate<SubmitForm>(validationSchema),
  });
  const { values, errors, touched, isValid } = formik;

  return (
    <Form onSubmit={formik.handleSubmit}>
      <div className="flow-content">
        <h1>Review and Submit</h1>
        <CustomInput
          className="mt-3"
          type="checkbox"
          id="personallyCertified"
          name="personallyCertified"
          label={
            <div>
              I understand that if this application leads to a partnership between my company and MindTrust, any false
              or misleading information or answer in this application may lead to the termination of our partnership
              agreement.
            </div>
          }
          onChange={() => {
            formik.setFieldValue('personallyCertified', !values.personallyCertified, true);
          }}
          checked={!!values.personallyCertified}
        />
        <FormGroup className="mt-3">
          <label htmlFor="legalName">
            Full name <span className="error-message">*</span>
          </label>
          <Input
            type="text"
            name="legalName"
            placeholder="Your full legal name"
            valid={touched.legalName && !errors.legalName}
            invalid={touched.legalName && !!errors.legalName}
            required
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={values.legalName}
          />
          <FormFeedback>{errors.legalName}</FormFeedback>
        </FormGroup>
        <FormGroup>
          <label htmlFor="date">Date</label>
          <Input
            type="text"
            name="date"
            valid={touched.date && !errors.date}
            invalid={touched.date && !!errors.date}
            required
            disabled
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={values.date}
          />
          <FormFeedback>{errors.legalName}</FormFeedback>
        </FormGroup>
      </div>
      <Row className="mt-4">
        <Col xs={6}>
          <Button className="btn-block c-gray" color="default" onClick={goBack}>
            <i className="btn-icon-back" />
            Back
          </Button>
        </Col>
        <Col xs={6}>
          <Button className="btn-block" type="submit" color="primary" disabled={!isValid}>
            Next
            <i className="btn-icon-next" />
          </Button>
        </Col>
      </Row>
    </Form>
  );
};
